import React, { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import Form from "../../components/Form";
import PropTypes from "prop-types";
import Paper from "../../components/Paper";
import { makeStyles } from "../../utils/styles";
import Typography from "../../components/Typography";
import Divider from "../../components/Divider";
import Button from "../../components/Button";
import ButtonGroup from "../../components/ButtonGroup";
import useTheme from "../../hooks/useTheme";
import { Col, Row } from "react-styled-flexboxgrid";
import { parseDataToOptions } from "../../utils/helpers";
import Box from "../../components/Box";
import Select from "../../components/Select";
import FormLabel from "../../components/FormLabel";
import DatePicker from "../../components/DatePicker";
import Icon from "../../components/Icon";
import IconWrap from "../../components/IconWrap";
import { addDays, differenceInDays, fromUnixTime, getUnixTime } from "date-fns";
import Input from "../../components/Input";
import Loader from "../../components/Loader";
import { FORM, ROLES } from "../../utils/constans";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApi } from "../../hooks/useApi";
import { ApiDepartments } from "../../api/departments";
import { ApiProfessions } from "../../api/professions";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((css) => ({
    paper: css`
        position: relative;
        overflow: hidden;
        width: 920px;
        padding: 30px 30px 15px 30px;
    `,
    dividerHeader: css`
        margin-top: 25px;
        margin-bottom: 30px;
    `,
    dividerFooter: css`
        margin-top: 30px;
        margin-bottom: 15px;
    `,
    buttonsFooter: css`
        justify-content: flex-end;
    `,
    boxLeft: css`
        margin-right: 40px;
    `,
    boxRight: css`
        margin-left: 40px;
    `,
    datePickers: css`
        display: flex;
        align-items: center;
    `,
    datePickersSeparator: css`
        margin: 0 12px 20px 12px;
    `,
    starsContainer: css`
        margin-bottom: 28px;
    `,
    stars: css`
        margin-top: 23px;
    `
}));

const schemaBasic = yup.object().shape({
    name: yup.string(),
    profession_id: yup.string(),
    time_start_from: yup.number().typeError(FORM.VALUE_MUST_BE_NUMBER),
    time_start_to: yup.number().typeError(FORM.VALUE_MUST_BE_NUMBER),
    place_id: yup.string().required(FORM.FIELD_IS_REQUIRED),
    department_id: yup.string()
});

const getPreloadedValues = (date) => ({
    name: '',
    profession_id: '',
    time_start_from: getUnixTime(addDays(date, -7)),
    time_start_to: getUnixTime(date),
    place_id: '',
    department_id: ''
});

const FormGenerateRaport = ({ role, loading, onSubmit, onClickCancel, initialValues, places }) => {
    const { t } = useTranslation()
    const styles = useStyles();
    const theme = useTheme();
    const { handleSubmit, errors, control, watch, setValue, register } = useForm({
        resolver: yupResolver(schemaBasic),
        defaultValues: {
            ...getPreloadedValues(new Date()),
            ...initialValues
        }
    });

    const [{ data: departments, loading: departmentsLoading }, getDepartments] = useApi(ApiDepartments.get, { manual: true });
    const [{ data: professions, loading: professionsLoading }] = useApi(ApiProfessions.get);

    const watchStartDate = fromUnixTime(watch('time_start_from'));
    const watchEndDate = fromUnixTime(watch('time_start_to'));
    const watchPlaceID = watch("place_id");

    const professionsOptions = useMemo(
        () => (
            professions && parseDataToOptions(professions)
        ),
        [professions]
    );

    const departmentsOptions = useMemo(
        () => (
            departments && parseDataToOptions(departments)
        ),
        [departments]
    );

    const placesOptions = useMemo(
        () => (
            places && parseDataToOptions(places)
        ),
        [places]
    );

    useEffect(() => {
        if (watchPlaceID) {
            getDepartments({ place_id: watchPlaceID });
        }
    }, [watchPlaceID, getDepartments]);

    useEffect(() => {
        register({ name: "profession_id" });
        register({ name: "time_start_from" });
        register({ name: "time_start_to" });
        register({ name: "place_id" });
        register({ name: "department_id" });
    }, [register]);

    const handleChangeStartDate = date => setValue('time_start_from', getUnixTime(date));
    const handleChangeEndDate = date => setValue('time_start_to', getUnixTime(date));

    useEffect(() => {
        if (differenceInDays(watchStartDate, watchEndDate) > 0) {
            setValue('time_start_to', getUnixTime(watchStartDate));
        }
    }, [watchStartDate, watchEndDate, setValue]);

    return (
        <Paper
            customStyle={styles.paper}
        >
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Typography as="h1">
                    {t("Nowy raport")}
                </Typography>
                <Divider customStyle={styles.dividerHeader} />
                <Input
                    type="text"
                    ref={register}
                    name="name"
                    label={t("Nazwa raportu")}
                    placeholder={t("Opcjonalnie")}
                />
                <Divider customStyle={styles.dividerHeader} />
                <Row>
                    <Col xs={6}>
                        <Box customStyle={styles.boxLeft}>
                            <Controller
                                control={control}
                                name="profession_id"
                                render={({ onChange, value }) => (
                                    <Select
                                        label={t("Zawód kandydatów")}
                                        placeholder={t("Wszystkie")}
                                        value={value}
                                        onChange={onChange}
                                        options={professionsOptions}
                                        fullWidth
                                        firstEnable
                                        disabled={professionsLoading}
                                    />
                                )}
                            />
                            <FormLabel>{t("Data świadczenia pracy od - do")}</FormLabel>
                            <Box customStyle={styles.datePickers}>
                                <DatePicker
                                    selected={watchStartDate}
                                    onChange={handleChangeStartDate}
                                    selectsStart
                                    startDate={watchStartDate}
                                    endDate={watchEndDate}
                                />
                                <IconWrap customStyle={styles.datePickersSeparator}>
                                    <Icon name="minus" width={8} height={8} fill={theme.colors.darkGrey} />
                                </IconWrap>
                                <DatePicker
                                    selected={watchEndDate}
                                    onChange={handleChangeEndDate}
                                    selectsEnd
                                    startDate={watchStartDate}
                                    endDate={watchEndDate}
                                    minDate={watchStartDate}
                                />
                            </Box>
                        </Box>
                    </Col>
                    <Col xs={6}>
                        <Box customStyle={styles.boxRight}>
                            {
                                role === ROLES.SUPERADMIN &&
                                <Controller
                                    control={control}
                                    name="place_id"
                                    render={({ onChange, value }) => (
                                        <Select
                                            label={t("Obiekt")}
                                            placeholder={t("Wybierz")}
                                            value={value}
                                            onChange={onChange}
                                            options={placesOptions}
                                            fullWidth
                                            error={!!errors.place_id}
                                            errorText={errors.place_id?.message}
                                        />
                                    )}
                                />
                            }
                            <Controller
                                control={control}
                                name="department_id"
                                render={({ onChange, value }) => (
                                    <Select
                                        label={t("Dział")}
                                        placeholder={t("Wszystkie")}
                                        value={value}
                                        onChange={onChange}
                                        options={departmentsOptions}
                                        fullWidth
                                        firstEnable
                                        disabled={departmentsLoading}
                                    />
                                )}
                            />
                        </Box>
                    </Col>
                </Row>
                <Divider customStyle={styles.dividerFooter} />
                <ButtonGroup customStyle={styles.buttonsFooter}>
                    <Button
                        variant="text"
                        onClick={onClickCancel}
                        startIcon={
                            <Icon
                                name="close"
                                width={12}
                                height={12}
                                fill={theme.colors.purple}
                            />
                        }
                    >
                        {t("Anuluj")}
                    </Button>
                    <Button
                        type="submit"
                        startIcon={<Icon name="report" width={12} height={12} fill={theme.colors.white} />}
                        disabled={loading}
                    >
                        {t("Generuj raport ze zlecenia")}
                    </Button>
                </ButtonGroup>
            </Form>
            <Loader show={loading} />
        </Paper>
    );
};

FormGenerateRaport.propTypes = {
    role: PropTypes.string,
    loading: PropTypes.bool,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    initialValues: PropTypes.object,
    places: PropTypes.array
};

export default FormGenerateRaport;
